import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { sendValidationCode, validateCode } from 'src/features/auth/auth.slice';
import { useAppDispatch } from 'src/store';
import { OtpField } from './OtpField';
import { PhoneField } from './PhoneField';

export const PhoneVerification = ({
  initialPhoneNumber = '',
  initialErrors = null,
  existingUser = null,
  email = null,
  onPhoneNumberChange = (phoneNumber) => { },
  onPhoneVerification = (phoneNumber, token, isExistingUser) => { },
  onErrorsChange = (fieldName, error) => { },
}) => {
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber);
  const [showOtpField, setShowOtpField] = useState(false);
  const [errors, setErrors] = useState(initialErrors);
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    setErrors(initialErrors);
  }, [initialErrors]);

  const handlePhoneSubmit = (phone) => {
    // Ensure the phone number is in E164 format
    // If it doesn't start with +, assume it needs formatting
    let formattedPhone = phone;
    
    // Clean the phone number of any non-digit characters except the leading +
    if (!phone.startsWith('+')) {
      // If no country code, default to US (+1)
      const cleaned = phone.replace(/\D/g, '');
      formattedPhone = '+1' + cleaned;
    } else {
      // Keep the + and remove any other non-digit characters
      const cleaned = phone.replace(/[^\d+]/g, '');
      formattedPhone = cleaned;
    }

    // Validate specific country formats
    if (formattedPhone.startsWith('+55')) {
      const digitsAfterCode = formattedPhone.substring(3).length;
      if (digitsAfterCode !== 11) {
        const errorMsg = 'Brazilian phone numbers must contain 11 digits after +55';
        setErrors(errorMsg);
        onErrorsChange('phone', errorMsg);
        return;
      }
    }

    onPhoneNumberChange(formattedPhone);
    setPhoneNumber(formattedPhone);
    setLoading(true);
    setErrors(null);

    const sendParams: any = { phone: formattedPhone };
    if (email) {
      sendParams.email = email;
    }
    // Pass forLogin parameter when existingUser is true (for login scenarios)
    if (existingUser === true) {
      sendParams.forLogin = true;
    }

    dispatch(sendValidationCode(sendParams))
      .unwrap()
      .then(() => {
        setShowOtpField(true);
        setLoading(false);
      })
      .catch((error) => {
        // Check if this is an existing user error
        const errorMessage = error?.toString() || '';
        const isExistingUserError = errorMessage.includes('already associated with an existing account') ||
                                   errorMessage.includes('already registered');

        if (isExistingUserError) {
          // For login scenarios, existing user error is expected, so don't redirect
          if (existingUser === true) {
            // For login, we expect the user to exist, so proceed with the flow
            setShowOtpField(true);
            setLoading(false);
            return;
          } else {
            // For registration, redirect to login page
            sessionStorage.setItem('loginError', 'User already exists. Please log in instead.');
            window.location.href = '/login';
            return;
          }
        }

        setErrors(error);
        onErrorsChange('phone', error);
        setLoading(false);
      });
  };

  const handleOtpSubmit = async (code) => {
    setErrors(null);
    const validateParams: any = { phone: phoneNumber, code: code };
    if (email) {
      validateParams.email = email;
    }
    // Pass forLogin parameter when existingUser is true (for login scenarios)
    if (existingUser === true) {
      validateParams.forLogin = true;
    }

    dispatch(validateCode(validateParams))
      .unwrap()
      .then((response) => {
        // For login scenarios (existingUser === true), we want to proceed even if user exists
        if (existingUser === true) {
          // For login, we expect the user to exist, so proceed with verification
          setIsPhoneVerified(true);
          onPhoneVerification(phoneNumber, response.data.token, response.data.existing_user);
        } else {
          // For registration scenarios, check if user already exists
          if (response.data.existing_user) {
            const reason = response.data.existing_user_reason;
            const existingEmail = response.data.existing_user_email;

            if (reason === 'phone_number') {
              setErrors(`Phone number ${phoneNumber} is already associated with account ${existingEmail}. Please log in instead.`);
            } else if (reason === 'email') {
              setErrors(`Email ${email} is already registered. Please log in instead.`);
            } else {
              setErrors('User already exists with this phone number or email. Please log in instead.');
            }

            // Store error for login page
            sessionStorage.setItem('loginError', 'User already exists. Please log in instead.');
          } else if (existingUser != null && !existingUser && response.data.existing_user) {
            setErrors('User already exists with this phone number.');
          } else if (existingUser != null && existingUser && !response.data.existing_user) {
            setErrors('User not found with this phone number.');
          } else {
            setIsPhoneVerified(true);
            onPhoneVerification(phoneNumber, response.data.token, response.data.existing_user);
          }
        }
        setShowOtpField(false);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        onErrorsChange('otp', error);
        setErrors(error);
      });
  };

  const handleChangePhoneNumber = () => {
    setErrors(null);
    onErrorsChange('otp', null);
    setShowOtpField(false);
    setIsPhoneVerified(false);
  };

  return (
    <>
      {showOtpField ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 2, 
          alignItems: 'center',
          textAlign: 'center'
        }}>
          <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Typography color="text.secondary" variant="body1">
              Verify {phoneNumber}
            </Typography>
            <Typography
              variant="body2"
              color="primary"
              sx={{ cursor: 'pointer' }}
              onClick={handleChangePhoneNumber}
            >
              Change
            </Typography>
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
          >
            Check your phone for a verification code
          </Typography>
          <OtpField
            error={errors}
            loading={loading}
            phoneNumber={phoneNumber}
            onSubmit={handleOtpSubmit}
            onSendAgain={handlePhoneSubmit}
          />
        </Box>
      ) : (
        <PhoneField
          initialError={errors}
          loading={loading}
          initialPhoneNumber={phoneNumber}
          onPhoneNumberChange={onPhoneNumberChange}
          onSubmit={handlePhoneSubmit}
          isVerified={isPhoneVerified}
        />
      )}
    </>
  );
};
